import { QueryCache, MutationCache, QueryClient } from "@tanstack/react-query";
import { captureException } from "@/lib/analytics/client";

/**
 * Report a failed query/mutation to Sentry. This restores the error-telemetry
 * parity that the server `withAction` wrapper provided: now that reads/writes
 * run client-side via React Query + the Supabase SDK, their failures no longer
 * pass through `withAction`, so we forward them here. `captureException`
 * delegates to the shared `clientLogger` (→ Sentry) and is a no-op until Sentry
 * has initialized (dev / SSR / no DSN), and never throws.
 */
function reportError(
  error: unknown,
  context: Record<string, unknown>,
): void {
  captureException(error, { source: "react-query", ...context });
}

export function makeQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        reportError(error, { kind: "query", queryKey: query.queryKey });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        reportError(error, {
          kind: "mutation",
          mutationKey: mutation.options.mutationKey ?? null,
        });
      },
    }),
    defaultOptions: {
      queries: {
        // Always treat cached data as stale so any subscribe/mount triggers
        // a network refetch when online. The query function still returns
        // cached data as a fallback when the network call fails (offline).
        staleTime: 0,
        gcTime: 300_000,
        refetchOnMount: "always",
        refetchOnWindowFocus: true,
        refetchOnReconnect: "always",
        // No point retrying while the browser knows it is offline — the
        // queryFn's own Dexie branch has already served what it can, and
        // retrying just burns three timers per query on every mount.
        retry: (failureCount) => {
          if (typeof window !== "undefined" && !window.navigator.onLine) {
            return false;
          }
          return failureCount < 3;
        },
        // `offlineFirst`, NOT `online` (audit OFF-02).
        //
        // The Dexie read path lives *inside* the query functions:
        // `createPersistedQuery` checks `navigator.onLine` and falls back to
        // `cacheReader`. Under `networkMode: 'online'`, TanStack Query v5's
        // `onlineManager` flips on the browser `offline` event and PAUSES the
        // query — the `queryFn` never runs at all, so that fallback is
        // unreachable. Any page first mounted after connectivity dropped sat at
        // `fetchStatus: 'paused'` showing skeletons forever instead of the data
        // already sitting in IndexedDB.
        //
        // The persistence layer only ever worked on a cold load that *began*
        // offline, because v5's `onlineManager` initialises to `true` — which is
        // precisely the scenario it wasn't built for. `offlineFirst` lets the
        // `queryFn` always run and decide for itself between the backend and
        // Dexie.
        networkMode: "offlineFirst",
      },
      mutations: {
        networkMode: "offlineFirst",
      },
    },
  });
}
